import ErrorPopup from '@/components/custom/InfoPopups/ErrorPopup';
import SuccessPopup from "@/components/custom/InfoPopups/SuccessPopup";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useCreateCrm } from '@/features/crm/hooks/useMutation/useCreateCrm';
import { useCreateCrmProject } from '@/features/crm_project/hooks/useMutation/useCreateCrmProject';
import { useEditMultipleProjectsWithDifferentData } from '@/lib/useQuery/project/useProjects';
import { ConfigurationParameterKey } from '@/types/crm/configuration/configuration';
import { zodResolver } from '@hookform/resolvers/zod';
import { Info } from 'lucide-react';
import { useFieldArray, useForm } from "react-hook-form";
import { useParams } from 'react-router-dom';
import { type ParameterFormSchemaType as step3SchemaType } from '../../../../../../parameters/components/organisms/ParameterForm/ParameterForm.types';
import { schema, type schemaType } from './schema';
import { OptionsFieldArray } from './Step4OptionsFieldArray';


export type Project = {
    id: string;
    name: string;
}

type Props = {
    data: {
        1: {
            data: Project[];
        };
        2: {
            data: {
                name: string;
                system: string;
            };
        };
        3: {
            data: step3SchemaType;
        };
    };
    onBack: () => void;
    onClose: () => void;
}

const Step4: React.FC<Props> = ({
    data,
    onBack,
    onClose,
}) => {
    const { orgId } = useParams();
    const { mutateAsync: createCrm } = useCreateCrm();
    const { mutateAsync: createMultipleCrmProjects } = useCreateCrmProject();
    const { mutateAsync: associateCrmConfigurationsWithProjects } = useEditMultipleProjectsWithDifferentData();

    const {
        handleSubmit,
        formState,
        register,
        control,
    } = useForm<schemaType>({ 
        resolver: zodResolver(schema),
        defaultValues: {
            projects: data[1].data.map((project) => ({
                id: project.id,
                name: project.name,
                elements: data[3]?.data?.parameter?.project?.map( (parameter) => ({
                    name: parameter.name,
                    value: "",
                    id: parameter.id,
                    options: parameter.options,
                }))
            }))
        }
    });
    const { errors } = formState;
    const { fields } = useFieldArray({
        control,
        name: 'projects',
    });

    const getConfigurationData = ({
        parameterName,
        crmParametersData
    }: {
        parameterName: ConfigurationParameterKey,
        crmParametersData: step3SchemaType
    }) => {
        const configurationData = crmParametersData.parameter?.[parameterName] || [];

        return configurationData?.reduce((acc, parameter) => {
            return {
                ...acc,
                [String(parameter.id)]: parameter.options || []
            }
        }, {});
    }

    const submitAllData = async (step4Data: schemaType) => {
        const selectedProjects = data[1].data;
        const crmBasicData = data[2].data;
        const crmParametersData = data[3].data;
     
        // 1. create crm configuration
        const reqBody = {
            name: crmBasicData.name,
            crm_system: crmBasicData.system,
            configuration: {
                parameter: {
                    project: crmParametersData.parameter?.project?.map((parameter) => ({ name: parameter.name, id: parameter.id, settings: parameter.settings })) || [],
                    survey: crmParametersData.parameter?.survey?.map((parameter) => ({ name: parameter.name, id: parameter.id, settings: parameter.settings })) || [],
                    question: crmParametersData.parameter?.question?.map((parameter) => ({ name: parameter.name, id: parameter.id, settings: parameter.settings })) || [],
                }
            },
            configuration_data: {
                parameter: {
                    project: getConfigurationData({parameterName: 'project', crmParametersData}),
                    survey: getConfigurationData({parameterName: 'survey', crmParametersData}),
                    question: getConfigurationData({parameterName: 'question', crmParametersData}),
                }
            },
            default_configuration: {}
        }

        let createdCrmData = null;
        try {
            createdCrmData = await createCrm({...reqBody, organisation_id: Number(orgId)});
        } catch (error: URIError | unknown) {
            if (error instanceof Error) {
                ErrorPopup(error.message);
            } else {
                ErrorPopup("An unknown error occurred");
            }
        }
        const crmId = createdCrmData?.data?.id;

        if(crmId && step4Data?.projects) {
            // 2. create crm_project for each project
            const projectCRMSufix = ' - CRM Configuration';
            const reqBody = step4Data.projects.map((project) => {
                return {
                    name:`${project.name}${projectCRMSufix}`,
                    crm_id: crmId,
                    selected_configuration: {
                        parameter: project?.elements?.map((element) => ({
                            id: element.id,
                            value: element.value
                        }))
                    }
                }
            })
            
            let crmProjectResult = null;
            try {
                crmProjectResult = await createMultipleCrmProjects(reqBody);
            } catch (error: URIError | unknown) {
                if (error instanceof Error) {
                    ErrorPopup(error.message);
                } else {
                    ErrorPopup("An unknown error occurred");
                }
            }
            const crmProjectsData = crmProjectResult?.data;
            
            if(crmProjectsData) {
                const getProjectCRMidByName = (projectName: string, responseBody: {name: string, id: number}[]) => {
                    return responseBody.find((project) => project.name === `${projectName}${projectCRMSufix}`)?.id;
                }

                // 3. associate projects with created crm configurations
                const reqBody = selectedProjects.map((project) => {
                    return {
                        id: project.id,
                        crm_id: crmId,
                        crm_system: crmBasicData.system,
                        crm_project_id: getProjectCRMidByName(project.name, crmProjectsData),
                    }
                });
                
                try {
                    await associateCrmConfigurationsWithProjects({
                        data: reqBody,
                    });
                    SuccessPopup("CRM configured successfully");
                    onClose();
                } catch (error: URIError | unknown) {
                    if (error instanceof Error) {
                        ErrorPopup(error.message);
                    } else {
                        ErrorPopup("An unknown error occurred");
                    }
                }
            }
        }
    }

    const onSubmitHandler = () => {
        handleSubmit((step4Data) => {
            submitAllData(step4Data);
        })();
    }

    return (
        <>
            <div className="flex items-center my-2 text-blue-600">
                <Info className="flex-shrink-0 me-1" size="16"/>
                <p className="text-xs">Salesforce parameters are defined based on a particular object’s properties. These object properties can be associated at the project, survey, or question level from Collaborate. Please add all the salesforce properties that will allow your Collaborate Project to connect to your Salesforce Page object.</p>
            </div>

            <div className="flex flex-col gap-3">

                <Accordion type="multiple">
                    { fields.map((project, index) => (
                        <AccordionItem
                            value={String(index)}
                            key={index}
                            className="mb-3 border-none"
                        >
                            <Card className="px-3">
                                <AccordionTrigger>
                                    <div className="flex flex-col items-start">
                                        <h2>{project.name}</h2>
                                    </div>
                                </AccordionTrigger>
                        
                                <AccordionContent className="px-1 space-y-2">
                                    <OptionsFieldArray 
                                        control={control}
                                        index={index}
                                        register={register}
                                        errors={errors}
                                    />
                                </AccordionContent>
                            </Card>
                        </AccordionItem>
                    ))}
                </Accordion>

            </div>
            <div className="flex items-stretch justify-between w-full pt-3 m-0 mt-3 border-t sm:space-x-2 max-sm:flex-col max-sm:space-y-2">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                
                <div className="flex m-0 sm:space-x-2 max-sm:flex-col max-sm:space-y-2">
                    <Button variant="secondary" onClick={onBack}>Back</Button>
                    <Button onClick={onSubmitHandler}>Next</Button>
                </div>
            </div>
        </>
    )
}

export { Step4 };

